import { Component, OnInit, ViewChild } from '@angular/core';

import { Observable, forkJoin } from 'rxjs';

import { TwcPopupComponent } from '@topcon/twc-angular';

import { ToastService } from '@mirage-projects/cw-commons';
import { PermAccessLevel, UserResponseData } from '@mirage-projects/cw-models';

import { PermissionsEditorService } from '../permissions-editor.service';

/**
 * This component displays the User list popup used in the permissions editor
 */
@Component({
    templateUrl: './add-user-popup.component.html',
    styleUrls: ['../permissions-element-list.scss'],
    standalone: false,
})
export class AddUserPopupComponent implements OnInit {
    userList: UserResponseData[] | undefined;
    selectedUsers: string[] = [];
    @ViewChild(TwcPopupComponent, { static: false }) addUserPopup!: TwcPopupComponent;

    constructor(
        public permissionsEditorService: PermissionsEditorService,
        private toastService: ToastService
    ) {}

    ngOnInit(): void {
        this.userList = this.permissionsEditorService.allUsers;
    }

    getUserName(userUid: string): string {
        const user = this.permissionsEditorService.allUsers.find((u) => u.uid === userUid);
        return user ? this.permissionsEditorService.getFullName(user) : '';
    }

    /**
     * If the user is already added to the exceptions list we do nothing
     * If the user is selected, it will be removed from the selectedUsers list
     * If the user is not selected, it will be added to the selectedUsers list
     * @param userUid: User uid
     */
    onSelect(userUid: string): void {
        if (this.isUserAlreadyAdded(userUid)) {
            return;
        }
        if (this.isSelectedUserId(userUid)) {
            this.selectedUsers = this.selectedUsers.filter((uId) => uId !== userUid);
        } else {
            this.selectedUsers.push(userUid);
        }
    }

    closePopup(): void {
        this.addUserPopup.onClose();
    }

    /**
     * Add Users selecteds to the Users exceptions list adding the permissions.
     * When all permissions are added, the user list will be updated
     */
    addUsers(): void {
        this.closePopup();
        const observablesAddPerm: Observable<void>[] = this.selectedUsers.map((userId) =>
            this.permissionsEditorService.addUserPermission(userId, PermAccessLevel.READONLY)
        );
        this.permissionsEditorService.isLoadingUserList = true;

        forkJoin(observablesAddPerm).subscribe({
            next: () => {
                this.toastService.generic('PERMISSION_DIALOG.ADDED_USER_PERMISSIONS');
                this.permissionsEditorService.loadUsers();
            },
            error: () => {
                this.permissionsEditorService.loadUsers();
                this.toastService.error('PERMISSION_DIALOG.ERROR_ADDED_USER_PERMISSIONS');
            },
        });
    }

    /**
     * Returns true if the user is already added to the exceptions list
     */
    isUserAlreadyAdded(userId: string): boolean {
        return this.permissionsEditorService.userPermissionList.some((u) => u.id === userId);
    }

    isSelectedUserId(userId: string): boolean {
        return this.selectedUsers.some((uId) => uId === userId);
    }
}
